import { uiLogger } from '../utils/loggerInstance';
import { triggerAuthError } from './tokenRefreshRegistry';

/**
 * Utility for handling authentication and authorization errors consistently.
 */

/**
 * Check if the error is a 401 Unauthorized error.
 */
export const isUnauthorizedError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') return false;

  const err = error as any;

  // Check HTTP status code
  if (err.response?.status === 401) {
    return true;
  }

  // Check error message content for JWT/Auth indicators
  if ('message' in err && typeof err.message === 'string') {
    const message = err.message || '';
    return (
      message.includes('Token') || message.includes('JWT') || message.includes('authentication')
    );
  }

  return false;
};

/**
 * Check if the error is a 403 Forbidden error.
 */
export const isForbiddenError = (error: unknown): boolean => {
  return (error as any)?.response?.status === 403;
};

/**
 * Standard parameters for auth error handling
 */
interface AuthErrorHandleParams {
  error: unknown;
  context: string;
  onUnauthorized?: () => void;
  onForbidden?: () => void;
  shouldSuppressRedirect?: boolean;
}

/**
 * Centralized handler for auth-related errors.
 * Logs the error and performs standardized actions (like redirecting or clearing cache).
 */
export const handleAuthError = (params: AuthErrorHandleParams): boolean => {
  const { error, context, onUnauthorized, onForbidden, shouldSuppressRedirect = false } = params;

  if (isUnauthorizedError(error)) {
    uiLogger.warn(`Authentication error while ${context}: 401 Unauthorized`, {
      context,
      error,
    });

    // Clear service worker cache on auth error
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHE' });
      uiLogger.debug('Cache clear message sent to service worker');
    }

    if (onUnauthorized) {
      onUnauthorized();
    }

    if (!shouldSuppressRedirect) {
      triggerAuthError();
    }

    return true;
  }

  if (isForbiddenError(error)) {
    const err = error as any;
    const permissionMessage =
      err?.response?.data?.message ||
      err?.response?.data?.detail ||
      err?.message ||
      'Permission denied';

    uiLogger.warn(`Permission error while ${context}: 403 Forbidden. Session remains active.`, {
      context,
      details: permissionMessage,
      error,
    });

    if (onForbidden) {
      onForbidden();
    }

    return true;
  }

  return false;
};
